import type { PageModule } from '../router'
import { icon, type IconName } from './icons'
import {
  init_shell,
  nav_meta_for_path,
  sync_shell_active_path,
  workspace_shell_html
} from './shell'
import {
  load_workspace_tabs_local,
  load_workspace_tabs_remote,
  schedule_persist_workspace_tabs,
  type WorkspaceTabsSnapshot
} from './workspace-persistence'

export type WorkspaceRouteLoader = () => Promise<PageModule>

type WorkspaceTab = {
  path: string
  title: string
  icon: IconName
  panel: HTMLElement
  module: PageModule | null
  mounted: boolean
  last_active_at: number
}

const MAX_TABS = 12
const DEFAULT_PATH = '/'

/**
 * Paths tabáveis do workspace autenticado.
 * REGRA (AGENTS-UI.md): rota autenticada nova = router.register + ESTE set no mesmo commit.
 * Path logado fora daqui vira standalone (sem abas) — bug de produto.
 */
export const WORKSPACE_PATHS = new Set<string>([
  '/',
  '/modules',
  '/conformidade',
  '/gap-analysis',
  '/auditorias',
  '/nao-conformidades',
  '/modules/m01',
  '/modules/m02',
  '/modules/m03',
  '/modules/m04',
  '/modules/m05',
  '/modules/m06',
  '/modules/m07',
  '/modules/m08',
  '/modules/m09',
  '/modules/m10',
  '/modules/m11',
  '/modules/m12',
  '/modules/m13',
  '/modules/m14',
  '/modules/m15',
  '/modules/m16',
  '/modules/m17',
  '/modules/m18',
  '/modules/m19',
  '/modules/m20',
  '/modules/m21',
  '/modules/m22',
  '/modules/m23',
  '/modules/m24',
  '/modules/m25',
  '/modules/m26',
  '/modules/m27',
  '/modules/m28',
  '/admin/users',
  '/admin/acl',
  '/admin/platform',
  '/admin/integrations',
  '/account',
  '/privacy',
  '/security',
  '/onboarding'
])

let root_el: HTMLElement | null = null
let outlet_el: HTMLElement | null = null
let tab_bar_el: HTMLElement | null = null
const loaders = new Map<string, WorkspaceRouteLoader>()
let tabs: WorkspaceTab[] = []
let active_path: string | null = null
let user_id: string | null = null
let bootstrapped = false
let bootstrap_promise: Promise<void> | null = null
let tab_bar_listener_bound = false
let navigate_seq = 0

function escape_html(value: string): string {
  return String(value || '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')
}

export function is_workspace_path(path: string): boolean {
  const clean = normalize_path(path)
  return Boolean(clean && WORKSPACE_PATHS.has(clean))
}

export function is_workspace_active(): boolean {
  return bootstrapped && Boolean(root_el?.querySelector('[data-workspace="1"]'))
}

export function normalize_path(path: string): string {
  const clean = String(path || '').split('?')[0].split('#')[0].trim()
  if (!clean || clean === '') return '/'
  return clean.startsWith('/') ? clean : `/${clean}`
}

export function register_workspace_loader(path: string, loader: WorkspaceRouteLoader): void {
  loaders.set(normalize_path(path), loader)
}

export function register_workspace_loaders(entries: Array<{ path: string; loader: WorkspaceRouteLoader }>): void {
  for (const entry of entries) register_workspace_loader(entry.path, entry.loader)
}

function meta_for(path: string): { title: string; icon: IconName } {
  const meta = nav_meta_for_path(path)
  if (meta) return { title: meta.label, icon: meta.icon }
  if (path === '/account') return { title: 'Conta', icon: 'user' }
  if (path === '/modules') return { title: 'Mapa de módulos', icon: 'modules' }
  return { title: path, icon: 'modules' }
}

function close_mobile_drawer(): void {
  document.getElementById('nr01-drawer')?.classList.add('hidden')
}

function snapshot(): WorkspaceTabsSnapshot {
  return {
    tabs: tabs.map(t => ({ path: t.path, title: t.title })),
    active_path: active_path || tabs[0]?.path || DEFAULT_PATH
  }
}

function persist(): void {
  if (!user_id) return
  schedule_persist_workspace_tabs(user_id, snapshot())
}

function find_tab(path: string): WorkspaceTab | undefined {
  const clean = normalize_path(path)
  return tabs.find(t => t.path === clean)
}

function paint_tab_bar(): void {
  if (!tab_bar_el) return
  if (!tabs.length) {
    tab_bar_el.innerHTML = ''
    return
  }

  tab_bar_el.innerHTML = tabs.map(tab => {
    const active = tab.path === active_path
    const safe_path = escape_html(tab.path)
    const safe_title = escape_html(tab.title)
    return `
      <div
        class="nr01-tab group relative flex min-w-0 max-w-[14rem] shrink-0 items-stretch border-r border-border ${active ? 'nr01-tab--active bg-surface text-ink' : 'bg-transparent text-muted hover:bg-surface hover:text-ink'}"
        data-workspace-tab="${safe_path}"
        role="presentation"
      >
        <button
          type="button"
          role="tab"
          class="flex min-w-0 flex-1 items-center gap-1.5 px-2.5 text-left text-xs font-medium"
          data-workspace-tab-activate="${safe_path}"
          aria-selected="${active ? 'true' : 'false'}"
          title="${safe_title}"
        >
          <span class="shrink-0 opacity-80">${icon(tab.icon, 'h-3.5 w-3.5')}</span>
          <span class="min-w-0 truncate">${safe_title}</span>
        </button>
        <button
          type="button"
          class="nr01-tab-close grid w-7 shrink-0 place-items-center text-muted hover:bg-surface2 hover:text-ink"
          data-workspace-tab-close="${safe_path}"
          aria-label="Fechar ${safe_title}"
          title="Fechar"
        >
          ${icon('close', 'h-3 w-3')}
        </button>
        ${active ? '<span class="pointer-events-none absolute inset-x-0 bottom-0 h-0.5 bg-brand" aria-hidden="true"></span>' : ''}
      </div>
    `
  }).join('')
}

function ensure_tab_bar_listener(): void {
  if (tab_bar_listener_bound || !tab_bar_el) return
  tab_bar_el.addEventListener('click', (event) => {
    const target = event.target as HTMLElement | null
    if (!target) return

    const close_btn = target.closest<HTMLElement>('[data-workspace-tab-close]')
    if (close_btn) {
      event.preventDefault()
      event.stopPropagation()
      const path = close_btn.getAttribute('data-workspace-tab-close') || ''
      void close_tab(path)
      return
    }

    const activate_btn = target.closest<HTMLElement>('[data-workspace-tab-activate]')
    if (activate_btn) {
      event.preventDefault()
      const path = activate_btn.getAttribute('data-workspace-tab-activate') || ''
      void open_path(path, { push: true })
    }
  })
  tab_bar_listener_bound = true
}

function attach_active_panel(): void {
  if (!outlet_el || !active_path) return
  const active = find_tab(active_path)
  if (!active) return

  // Keep-alive com detach: só o painel ativo fica no document (evita colisão de id).
  for (const tab of tabs) {
    if (tab.path === active.path) continue
    if (tab.panel.parentElement) tab.panel.remove()
  }

  if (active.panel.parentElement !== outlet_el) {
    outlet_el.appendChild(active.panel)
  }
}

async function mount_tab(tab: WorkspaceTab): Promise<void> {
  if (tab.mounted) return
  const loader = loaders.get(tab.path)
  if (!loader) {
    tab.panel.innerHTML = `
      <div class="rounded-sm border border-border bg-surface p-4 text-sm text-ink">
        Rota sem loader: ${escape_html(tab.path)}
      </div>
    `
    tab.mounted = true
    return
  }

  tab.panel.innerHTML = `
    <div class="p-4 text-sm text-muted" data-workspace-loading>Carregando…</div>
  `
  try {
    const mod = await loader()
    tab.module = mod
    tab.panel.innerHTML = ''
    await mod.mount(tab.panel)
    tab.mounted = true
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Falha ao carregar aplicativo'
    tab.panel.innerHTML = `
      <div class="rounded-sm border border-border bg-surface p-4 text-sm text-ink">
        ${escape_html(message)}
      </div>
    `
    tab.mounted = true
  }
}

function create_tab(path: string): WorkspaceTab {
  const clean = normalize_path(path)
  const meta = meta_for(clean)
  const panel = document.createElement('div')
  panel.className = 'nr01-workspace-panel'
  panel.setAttribute('data-workspace-panel', clean)
  panel.setAttribute('data-workspace-path', clean)

  return {
    path: clean,
    title: meta.title,
    icon: meta.icon,
    panel,
    module: null,
    mounted: false,
    last_active_at: Date.now()
  }
}

function evict_lru_if_needed(): void {
  while (tabs.length >= MAX_TABS) {
    let victim: WorkspaceTab | null = null
    for (const tab of tabs) {
      if (tab.path === active_path) continue
      if (!victim || tab.last_active_at < victim.last_active_at) victim = tab
    }
    if (!victim) victim = tabs[0]
    destroy_tab(victim, false)
  }
}

function destroy_tab(tab: WorkspaceTab, update_ui: boolean): void {
  try {
    tab.module?.unmount?.()
  } catch {
    // ignore unmount errors
  }
  if (tab.panel.parentElement) tab.panel.remove()
  tabs = tabs.filter(t => t.path !== tab.path)
  if (update_ui) {
    paint_tab_bar()
    persist()
  }
}

async function activate_tab(path: string, opts: { push: boolean }): Promise<void> {
  const clean = normalize_path(path)
  const tab = find_tab(clean)
  if (!tab) return

  const seq = ++navigate_seq
  active_path = clean
  tab.last_active_at = Date.now()
  attach_active_panel()

  if (!tab.mounted) {
    await mount_tab(tab)
    if (seq !== navigate_seq) return
  }

  paint_tab_bar()
  sync_shell_active_path(clean)
  close_mobile_drawer()

  if (opts.push) {
    const current = window.location.pathname
    if (current !== clean) window.history.pushState({}, '', clean)
  }

  persist()
}

export async function open_path(
  path: string,
  opts: { push?: boolean; force?: boolean } = {}
): Promise<void> {
  if (!bootstrapped) throw new Error('Workspace not bootstrapped')
  const clean = normalize_path(path)
  if (!WORKSPACE_PATHS.has(clean)) {
    throw new Error(`Path não é do workspace: ${clean}`)
  }

  let tab = find_tab(clean)
  if (!tab) {
    evict_lru_if_needed()
    tab = create_tab(clean)
    tabs.push(tab)
    paint_tab_bar()
  }

  await activate_tab(clean, { push: opts.push !== false })
}

export async function close_tab(path: string): Promise<void> {
  const clean = normalize_path(path)
  const tab = find_tab(clean)
  if (!tab) return

  const was_active = active_path === clean
  const index = tabs.findIndex(t => t.path === clean)
  destroy_tab(tab, true)

  if (!tabs.length) {
    await open_path(DEFAULT_PATH, { push: true })
    return
  }

  if (was_active) {
    const neighbor = tabs[Math.max(0, index - 1)] || tabs[0]
    await activate_tab(neighbor.path, { push: true })
  } else {
    paint_tab_bar()
    persist()
  }
}

export async function bootstrap_workspace(opts: {
  root: HTMLElement
  user_id: string
  initial_path: string
}): Promise<void> {
  if (bootstrap_promise) return bootstrap_promise

  bootstrap_promise = (async () => {
    root_el = opts.root
    user_id = String(opts.user_id)
    const initial = normalize_path(opts.initial_path)
    const preferred = WORKSPACE_PATHS.has(initial) ? initial : DEFAULT_PATH

    root_el.innerHTML = workspace_shell_html(preferred)
    await init_shell()

    outlet_el = root_el.querySelector('#nr01-workspace-outlet')
    tab_bar_el = root_el.querySelector('#nr01-tab-bar')
    if (!outlet_el || !tab_bar_el) throw new Error('Workspace shell incomplete')

    ensure_tab_bar_listener()
    bootstrapped = true
    tabs = []
    active_path = null

    const local = load_workspace_tabs_local(user_id, WORKSPACE_PATHS)
    const remote = await load_workspace_tabs_remote(WORKSPACE_PATHS).catch(() => null)

    // Prefer remote quando existir; senão local; senão só a rota atual.
    const restored = remote || local
    const restore_paths = restored?.tabs.map(t => t.path).filter(p => WORKSPACE_PATHS.has(p)) || []

    if (!restore_paths.includes(preferred)) restore_paths.push(preferred)

    // Dedup preserving order
    const ordered: string[] = []
    const seen = new Set<string>()
    for (const p of restore_paths) {
      if (seen.has(p) || !WORKSPACE_PATHS.has(p)) continue
      seen.add(p)
      ordered.push(p)
      if (ordered.length >= MAX_TABS) break
    }

    if (!ordered.length) ordered.push(DEFAULT_PATH)

    for (const p of ordered) {
      tabs.push(create_tab(p))
    }

    // Deep link: URL manda na aba ativa quando for path do workspace.
    const target = WORKSPACE_PATHS.has(preferred)
      ? preferred
      : (ordered.includes(preferred) ? preferred : ordered[0])

    paint_tab_bar()
    await activate_tab(target, { push: false })

    // Garante URL coerente
    if (window.location.pathname !== target) {
      window.history.replaceState({}, '', target)
    }
  })()

  try {
    await bootstrap_promise
  } catch (err) {
    bootstrap_promise = null
    bootstrapped = false
    throw err
  }
}

export async function ensure_workspace_and_open(
  path: string,
  opts: { root: HTMLElement; user_id: string; push?: boolean }
): Promise<void> {
  if (!bootstrapped) {
    await bootstrap_workspace({
      root: opts.root,
      user_id: opts.user_id,
      initial_path: path
    })
    return
  }
  await open_path(path, { push: opts.push !== false })
}

export function destroy_workspace(): void {
  for (const tab of [...tabs]) {
    destroy_tab(tab, false)
  }
  tabs = []
  active_path = null
  outlet_el = null
  tab_bar_el = null
  root_el = null
  user_id = null
  bootstrapped = false
  bootstrap_promise = null
  tab_bar_listener_bound = false
  navigate_seq += 1
}

export function get_active_workspace_path(): string | null {
  return active_path
}
